import {
  AutomatedVerificationResponseSchema,
  GetUserMetadataResponseSchema,
  IdvPreference,
  VerificationRequestSchema,
} from "@amzn/client-workbench-api-model";
import { API } from "@aws-amplify/api";

import { API_METRICS_HANDLE } from "@/apis/constants";
import { Logger } from "@/apis/Logger";
import {
  publishQueryLatencyMetrics,
  publishQueryResultMetrics,
} from "@/apis/Metrics";
import { getStageConfig } from "@/helpers/stageConfig";

import { AMPLIFY_DEFAULT_API_NAME } from "../amplifyConfig";

/**
 * Base64 encoded full URL of the redirect.
 * To encode a new URL, in a nodejs console do `Buffer.from("https://amazon.com/").toString('base64')` and paste the result in below.
 * The redirect URL **MUST** be configured also in the IVV back end.
 */

export const POST_USER_VERIFICATION_METRIC_NAME = "PostUserVerification";
export const POST_USER_VERIFICATION_PATH = "/user/verification";

/**
 * Calls CWB to get the IVV IFrame URL ("EvaluateDocument" call to IVV).
 */
export const startIvv = async (): Promise<string> => {
  const startTime = performance.now();

  try {
    const body: VerificationRequestSchema = {
      clientApp: "DayOneOnboardingWizard",
      verificationType: "AUTOMATED_IVV_VERIFICATION",
    };
    const result = (await API.post(
      AMPLIFY_DEFAULT_API_NAME,
      POST_USER_VERIFICATION_PATH,
      { body }
    )) as AutomatedVerificationResponseSchema;

    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: POST_USER_VERIFICATION_METRIC_NAME,
      numFailures: 0,
      numSuccess: 1,
    });

    return `${result.redirectUri}${getStageConfig().ivvCallbackUrl}`;
  } catch (err) {
    void Logger.errorErrorLike(
      `${POST_USER_VERIFICATION_METRIC_NAME} failed API call.`,
      err
    );

    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: POST_USER_VERIFICATION_METRIC_NAME,
      numFailures: 1,
      numSuccess: 0,
    });
    return Promise.reject(err);
  } finally {
    publishQueryLatencyMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: POST_USER_VERIFICATION_METRIC_NAME,
      elapsedTime: performance.now() - startTime,
    });
  }
};

export const PUT_USER_VERIFICATION_METRIC_NAME = "PutUserVerification";
export const PUT_USER_VERIFICATION_PATH = "/user/verification";

export const abortIvv = async (): Promise<boolean> => {
  const startTime = performance.now();

  try {
    await API.put(AMPLIFY_DEFAULT_API_NAME, PUT_USER_VERIFICATION_PATH, {});

    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: PUT_USER_VERIFICATION_METRIC_NAME,
      numFailures: 0,
      numSuccess: 1,
    });

    return true;
  } catch (err) {
    void Logger.errorErrorLike(
      `${PUT_USER_VERIFICATION_METRIC_NAME} failed API call.`,
      err
    );

    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: PUT_USER_VERIFICATION_METRIC_NAME,
      numFailures: 1,
      numSuccess: 0,
    });
    return false;
  } finally {
    publishQueryLatencyMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: PUT_USER_VERIFICATION_METRIC_NAME,
      elapsedTime: performance.now() - startTime,
    });
  }
};

export const GET_USER_METADATA_API_METRIC_NAME = "GetUserMetadataAPICall";
export const GET_USER_METADATA_PATH = "/user/metadata";

/**
 * @see https://code.amazon.com/packages/ClientWorkbenchApiModel/blobs/9b2d9d5f19ae07885d3ca17bef32c51bf7e6827f/--/open-api-definition.yml#L700,L252-L283
 */
export const getIdvPreference = async (): Promise<IdvPreference> => {
  const startTime = performance.now();

  try {
    const result = (await API.get(
      AMPLIFY_DEFAULT_API_NAME,
      GET_USER_METADATA_PATH,
      {}
    )) as GetUserMetadataResponseSchema;
    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: GET_USER_METADATA_API_METRIC_NAME,
      numFailures: 0,
      numSuccess: 1,
    });
    return result.idvPreference;
  } catch (err) {
    void Logger.errorErrorLike(
      `${GET_USER_METADATA_API_METRIC_NAME} failed API call.`,
      err
    );
    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: GET_USER_METADATA_API_METRIC_NAME,
      numFailures: 1,
      numSuccess: 0,
    });
    return Promise.reject(err);
  } finally {
    publishQueryLatencyMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: GET_USER_METADATA_API_METRIC_NAME,
      elapsedTime: performance.now() - startTime,
    });
  }
};
